import React from 'react';
import { useSocialLinks, useContactInfo, useServices } from '../../hooks/useAdminData';
import './Footer.scss';

const Footer: React.FC = () => {
  const socialLinks = useSocialLinks();
  const contactInfo = useContactInfo();
  const services = useServices();
  
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          {/* Logo & Description */}
          <div className="footer-section">
            <div className="footer-logo">
              <img 
                src="/assets/images/logo.svg" 
                alt="Premium Pets Logo" 
                className="footer-logo-image"
              />
            </div>
            <p className="footer-description">
              Professional mobile pet grooming services delivered directly to your door. Quality care in the comfort of your home.
            </p>
            <div className="footer-rating">
              <span className="stars">⭐⭐⭐⭐⭐</span>
              <span className="rating-text">5-Star Happy Pets</span>
            </div>
          </div>
          
          {/* Quick Links */}
          <div className="footer-section">
            <h3 className="footer-title">Quick Links</h3>
            <ul className="footer-links">
              <li><button onClick={() => scrollToSection('home')} className="footer-link" aria-label="Go to Home section">Home</button></li>
              <li><button onClick={() => scrollToSection('about')} className="footer-link" aria-label="Go to About section">About</button></li>
              <li><button onClick={() => scrollToSection('services')} className="footer-link" aria-label="Go to Services section">Services</button></li>
              <li><button onClick={() => scrollToSection('pricing')} className="footer-link" aria-label="Go to Pricing section">Pricing</button></li>
              <li><button onClick={() => scrollToSection('contact')} className="footer-link" aria-label="Go to Contact section">Contact</button></li>
            </ul>
          </div>
          
          {/* Our Services */}
          <div className="footer-section">
            <h3 className="footer-title">Our Services</h3>
            <ul className="footer-services">
              {services.map((service) => (
                <li key={service.id}>{service.name}</li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info */}
          <div className="footer-section">
            <h3 className="footer-title">Contact Info</h3>
            <ul className="footer-contact">
              <li>Phone: {contactInfo.phone}</li>
              <li>Email: {contactInfo.email}</li>
              <li>Service Area: {contactInfo.serviceArea}</li>
              <li>Hours: {contactInfo.hours}, {contactInfo.hoursWeekend}</li>
            </ul>
            
            {/* Social Media */}
            <div className="social-links">
              {socialLinks.map((social) => (
                <a key={social.name} href={social.url} className="social-link" aria-label={social.name}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    {getSocialIcon(social.icon)}
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Premium Pets Mobile Grooming. All rights reserved.</p>
          <p className="footer-legal">
            <a href="/privacy-policy/" className="footer-legal-link">Privacy Policy</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

const getSocialIcon = (iconType: string) => {
  switch (iconType) {
    case 'facebook':
      return (
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      );
    case 'instagram':
      return (
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      );
    default:
      return null;
  }
};

export default Footer;
